import { useEffect, useRef } from 'react';
import socketService from '../services/socket';
import { useApp } from '../contexts/AppContext';
import { AppNotification } from '../types';

let notifCounter = 0;
const makeId = () => `notif-${Date.now()}-${++notifCounter}`;

export const useNotifications = () => {
  const { state, addNotification } = useApp();
  const prevStatusRef = useRef<Map<string, string>>(new Map());

  // Build a machineId→name map for notification messages
  const machineMapRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const map = new Map<string, string>();
    state.machines.forEach((m) => map.set(m.id, m.name));
    machineMapRef.current = map;
  }, [state.machines]);

  useEffect(() => {
    const handleProductionUpdate = (data: any) => {
      if (!data?.type) return;

      const machineName = data.nesting?.machineName
        || machineMapRef.current.get(data.nesting?.machineId || data.machineId)
        || 'Macchina';

      let notif: AppNotification | null = null;

      if (data.type === 'nesting_start') {
        notif = {
          id: makeId(),
          type: 'production_start',
          title: 'Nuova lavorazione avviata',
          body: `${machineName}: ${data.nesting?.code || 'Nesting'}`,
          machineId: data.nesting?.machineId || data.machineId,
          machineName,
          timestamp: new Date().toISOString(),
          read: false,
        };
      } else if (data.type === 'nesting_complete') {
        notif = {
          id: makeId(),
          type: 'production_complete',
          title: 'Lavorazione completata',
          body: `${machineName}: ${data.nesting?.code || 'Nesting'} — ${data.nesting?.status || 'COMPLETED'}`,
          machineId: data.nesting?.machineId || data.machineId,
          machineName,
          timestamp: new Date().toISOString(),
          read: false,
        };
      }

      if (notif) addNotification(notif);
    };

    const handleTelemetryUpdate = (data: any) => {
      if (!data?.machineId || !data?.status) return;
      const prevStatus = prevStatusRef.current.get(data.machineId);
      const machineName = machineMapRef.current.get(data.machineId) || 'Macchina';

      if (prevStatus && prevStatus !== data.status) {
        let notif: AppNotification | null = null;

        if (prevStatus !== 'offline' && data.status === 'offline') {
          notif = {
            id: makeId(),
            type: 'machine_offline',
            title: 'Macchina offline',
            body: `${machineName} non risponde`,
            machineId: data.machineId,
            machineName,
            timestamp: new Date().toISOString(),
            read: false,
          };
        } else if (prevStatus === 'offline' && data.status !== 'offline') {
          notif = {
            id: makeId(),
            type: 'machine_online',
            title: 'Macchina online',
            body: `${machineName} si e\u0300 riconnessa`,
            machineId: data.machineId,
            machineName,
            timestamp: new Date().toISOString(),
            read: false,
          };
        } else if (data.status === 'error') {
          notif = {
            id: makeId(),
            type: 'error',
            title: 'Errore macchina',
            body: `${machineName} ha segnalato un errore`,
            machineId: data.machineId,
            machineName,
            timestamp: new Date().toISOString(),
            read: false,
          };
        }

        if (notif) addNotification(notif);
      }

      prevStatusRef.current.set(data.machineId, data.status);
    };

    socketService.on('production_update', handleProductionUpdate);
    socketService.on('telemetry_update', handleTelemetryUpdate);

    return () => {
      socketService.off('production_update', handleProductionUpdate);
      socketService.off('telemetry_update', handleTelemetryUpdate);
    };
  }, [addNotification]);
};
